import { FunctionComponent, useLayoutEffect, useRef } from "react";

/** Grows the box to fit its content so nothing is hidden behind an inner scrollbar. */
const fitToContent = (textarea: HTMLTextAreaElement | null) => {
	if (!textarea) {
		return;
	}

	textarea.style.height = "auto";

	// Feature-checked: jsdom (tests) reports a scrollHeight of 0 for every element.
	if (textarea.scrollHeight > 0) {
		textarea.style.height = `${textarea.scrollHeight}px`;
	}
};

/**
 * The editable "why you, why now" box of the priming chat (PREC-3712).
 *
 * Deliberately uncontrolled: chat messages are stored as static nodes, so a
 * controlled value would close over stale state. The current text is pushed to
 * the page through `onChange` instead.
 */
export const ValuePropBox: FunctionComponent<{
	defaultValue: string;
	placeholder: string;
	onChange: (value: string) => void;
}> = ({ defaultValue, placeholder, onChange }) => {
	const boxRef = useRef<HTMLTextAreaElement>(null);

	// The AI draft arrives as `defaultValue`, so the first fit happens on mount.
	useLayoutEffect(() => {
		fitToContent(boxRef.current);
	}, [defaultValue]);

	return (
		<textarea
			ref={boxRef}
			className="input-styles mt-2.5 block min-h-[76px] w-full resize-none overflow-hidden rounded-[10px] border bg-white px-3 py-2.5 text-ssm leading-relaxed"
			defaultValue={defaultValue}
			placeholder={placeholder}
			onChange={(event) => {
				fitToContent(event.currentTarget);
				onChange(event.target.value);
			}}
		/>
	);
};
